import React, { useState, useEffect, useRef } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Lock, Brain, Send, MessageCircle, Camera, Video, Upload, Lightbulb, Sparkles, CheckCircle, DollarSign, Calendar, Target, Calculator, X, Zap } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Navbar from "@/components/Navbar";

const AIGenerator = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [workout, setWorkout] = useState(null);
  const [form, setForm] = useState({
    goals: "general fitness",
    duration: 30,
    equipment: "none",
    difficulty: "intermediate",
    focus_areas: []
  });

  const canUseGenerator = user?.subscription_tier !== "free";

  const generateWorkout = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API}/ai/generate-workout`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(form)
      });
      const data = await response.json();
      if (data.workout) {
        setWorkout(data.workout);
        toast.success("Workout generated!");
      }
    } catch (error) {
      toast.error("Failed to generate workout");
    } finally {
      setLoading(false);
    }
  };

  const focusOptions = ["chest", "back", "legs", "arms", "core", "shoulders", "glutes"];

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      
      <div className="pt-24 sm:pt-32 pb-24 sm:pb-12 px-6">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <h1 className="font-heading text-4xl md:text-5xl font-bold tracking-tight mb-4">
              AI WORKOUT <span className="gradient-text">GENERATOR</span>
            </h1>
            <p className="text-text-secondary mb-8">
              Get a personalized workout plan in seconds
            </p>

            {!canUseGenerator ? (
              <div className="bg-surface border border-border rounded-xl p-8 text-center">
                <Lock className="w-12 h-12 text-text-muted mx-auto mb-4" />
                <h3 className="font-heading text-xl font-bold mb-2">Upgrade Required</h3>
                <p className="text-text-secondary mb-4">
                  AI Workout Generator is available on Foundations plan and above
                </p>
                <Link to="/pricing" className="text-primary hover:underline">
                  View Plans
                </Link>
              </div>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Form */}
                <div className="bg-surface border border-border rounded-xl p-6">
                  <h3 className="font-heading text-xl font-bold mb-6">YOUR PREFERENCES</h3>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm text-text-secondary mb-2">Goals</label>
                      <input
                        type="text"
                        value={form.goals}
                        onChange={(e) => setForm({ ...form, goals: e.target.value })}
                        className="w-full bg-[#0B0F1A] border border-[#1E293B] rounded-lg px-4 py-3 text-white"
                        placeholder="e.g., build muscle, lose weight"
                        data-testid="ai-goals"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm text-text-secondary mb-2">Duration (minutes)</label>
                      <select
                        value={form.duration}
                        onChange={(e) => setForm({ ...form, duration: parseInt(e.target.value) })}
                        className="w-full bg-[#0B0F1A] border border-[#1E293B] rounded-lg px-4 py-3 text-white"
                        data-testid="ai-duration"
                      >
                        <option value={15}>15 minutes</option>
                        <option value={30}>30 minutes</option>
                        <option value={45}>45 minutes</option>
                        <option value={60}>60 minutes</option>
                      </select>
                    </div>
                    
                    <div>
                      <label className="block text-sm text-text-secondary mb-2">Equipment</label>
                      <select
                        value={form.equipment}
                        onChange={(e) => setForm({ ...form, equipment: e.target.value })}
                        className="w-full bg-[#0B0F1A] border border-[#1E293B] rounded-lg px-4 py-3 text-white"
                        data-testid="ai-equipment"
                      >
                        <option value="none">No equipment (bodyweight)</option>
                        <option value="dumbbells">Dumbbells</option>
                        <option value="full gym">Full gym</option>
                        <option value="resistance bands">Resistance bands</option>
                      </select>
                    </div>
                    
                    <div>
                      <label className="block text-sm text-text-secondary mb-2">Difficulty</label>
                      <select
                        value={form.difficulty}
                        onChange={(e) => setForm({ ...form, difficulty: e.target.value })}
                        className="w-full bg-[#0B0F1A] border border-[#1E293B] rounded-lg px-4 py-3 text-white"
                        data-testid="ai-difficulty"
                      >
                        <option value="beginner">Beginner</option>
                        <option value="intermediate">Intermediate</option>
                        <option value="advanced">Advanced</option>
                      </select>
                    </div>
                    
                    <div>
                      <label className="block text-sm text-text-secondary mb-2">Focus Areas</label>
                      <div className="flex flex-wrap gap-2">
                        {focusOptions.map(area => (
                          <button
                            key={area}
                            onClick={() => {
                              const newAreas = form.focus_areas.includes(area)
                                ? form.focus_areas.filter(a => a !== area)
                                : [...form.focus_areas, area];
                              setForm({ ...form, focus_areas: newAreas });
                            }}
                            className={`px-3 py-1 rounded-full text-sm capitalize transition-colors ${
                              form.focus_areas.includes(area)
                                ? "bg-primary text-white"
                                : "bg-[#1E293B] text-text-secondary hover:bg-[#2D3B4F]"
                            }`}
                            data-testid={`focus-${area}`}
                          >
                            {area}
                          </button>
                        ))}
                      </div>
                    </div>
                    
                    <button
                      onClick={generateWorkout}
                      disabled={loading}
                      className="w-full bg-primary text-white py-3 rounded-lg font-semibold hover:bg-blue-600 transition-colors disabled:opacity-50 mt-4"
                      data-testid="generate-btn"
                    >
                      {loading ? "Generating..." : "Generate Workout"}
                    </button>
                  </div>
                </div>

                {/* Result */}
                <div className="bg-surface border border-border rounded-xl p-6">
                  <h3 className="font-heading text-xl font-bold mb-6">YOUR WORKOUT</h3>
                  
                  {workout ? (
                    <div className="space-y-6">
                      <div>
                        <h4 className="font-heading text-2xl font-bold text-primary">{workout.title}</h4>
                        <p className="text-text-secondary">{workout.description}</p>
                      </div>
                      
                      {workout.warmup && (
                        <div>
                          <h5 className="font-semibold text-accent mb-2">Warm-up</h5>
                          <ul className="space-y-2">
                            {workout.warmup.map((ex, i) => (
                              <li key={i} className="text-text-secondary">
                                • {ex.exercise} - {ex.duration}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                      
                      {workout.main_workout && (
                        <div>
                          <h5 className="font-semibold text-primary mb-2">Main Workout</h5>
                          <ul className="space-y-3">
                            {workout.main_workout.map((ex, i) => (
                              <li key={i} className="bg-[#0B0F1A] rounded-lg p-3">
                                <p className="font-semibold">{ex.exercise}</p>
                                <p className="text-sm text-text-secondary">
                                  {ex.sets} sets × {ex.reps} | Rest: {ex.rest}
                                </p>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                      
                      {workout.cooldown && (
                        <div>
                          <h5 className="font-semibold text-success mb-2">Cool-down</h5>
                          <ul className="space-y-2">
                            {workout.cooldown.map((ex, i) => (
                              <li key={i} className="text-text-secondary">
                                • {ex.exercise} - {ex.duration}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                      
                      {workout.tips && (
                        <div className="border-t border-border pt-4">
                          <h5 className="font-semibold mb-2">Tips</h5>
                          <ul className="space-y-1">
                            {workout.tips.map((tip, i) => (
                              <li key={i} className="text-text-secondary text-sm">• {tip}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="text-center py-12">
                      <Brain className="w-16 h-16 text-text-muted mx-auto mb-4" />
                      <p className="text-text-secondary">
                        Configure your preferences and click generate
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </motion.div>
        </div>
      </div>
    </div>
  );
};

// ==================== AI COACH CHAT ====================
const AICoach = () => {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const messagesEndRef = useRef(null);

  const canUseCoach = user?.subscription_tier === "accelerator" || user?.subscription_tier === "elite";

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(scrollToBottom, [messages]);

  const sendMessage = async () => {
    if (!input.trim() || loading) return;

    const userMessage = { role: "user", content: input };
    setMessages(prev => [...prev, userMessage]);
    setInput("");
    setLoading(true);

    try {
      const response = await fetch(`${API}/ai/coach-chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ message: input, session_id: sessionId })
      });
      const data = await response.json();
      
      if (data.response) {
        setMessages(prev => [...prev, { role: "assistant", content: data.response }]);
        setSessionId(data.session_id);
      }
    } catch (error) {
      toast.error("Failed to get response");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      
      <div className="pt-24 sm:pt-32 pb-24 sm:pb-12 px-6">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <h1 className="font-heading text-4xl md:text-5xl font-bold tracking-tight mb-4">
              AI <span className="gradient-text">COACH</span>
            </h1>
            <p className="text-text-secondary mb-8">
              Your 24/7 fitness expert - ask anything about training, nutrition, and recovery
            </p>

            {!canUseCoach ? (
              <div className="bg-surface border border-border rounded-xl p-8 text-center">
                <Lock className="w-12 h-12 text-text-muted mx-auto mb-4" />
                <h3 className="font-heading text-xl font-bold mb-2">Upgrade Required</h3>
                <p className="text-text-secondary mb-4">
                  AI Coach is available on Advanced Accelerator plan and above
                </p>
                <Link to="/pricing" className="text-primary hover:underline">
                  View Plans
                </Link>
              </div>
            ) : (
              <div className="bg-surface border border-border rounded-xl overflow-hidden">
                {/* Chat Messages */}
                <div className="h-[500px] overflow-y-auto p-6 space-y-4">
                  {messages.length === 0 ? (
                    <div className="text-center py-12">
                      <MessageCircle className="w-16 h-16 text-text-muted mx-auto mb-4" />
                      <p className="text-text-secondary">
                        Start a conversation with Coach Alex
                      </p>
                      <div className="flex flex-wrap justify-center gap-2 mt-4">
                        {["How should I warm up?", "What's a good post-workout meal?", "Help me with my squat form"].map((q, i) => (
                          <button
                            key={i}
                            onClick={() => setInput(q)}
                            className="bg-[#1E293B] text-text-secondary px-3 py-1 rounded-full text-sm hover:bg-[#2D3B4F]"
                          >
                            {q}
                          </button>
                        ))}
                      </div>
                    </div>
                  ) : (
                    messages.map((msg, i) => (
                      <div
                        key={i}
                        className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}
                      >
                        <div
                          className={`max-w-[80%] rounded-2xl px-4 py-3 ${
                            msg.role === "user"
                              ? "bg-primary text-white"
                              : "bg-[#1E293B] text-text-primary"
                          }`}
                        >
                          <p className="whitespace-pre-wrap">{msg.content}</p>
                        </div>
                      </div>
                    ))
                  )}
                  {loading && (
                    <div className="flex justify-start">
                      <div className="bg-[#1E293B] rounded-2xl px-4 py-3">
                        <div className="flex gap-1">
                          <span className="w-2 h-2 bg-text-muted rounded-full animate-bounce"></span>
                          <span className="w-2 h-2 bg-text-muted rounded-full animate-bounce" style={{ animationDelay: "0.1s" }}></span>
                          <span className="w-2 h-2 bg-text-muted rounded-full animate-bounce" style={{ animationDelay: "0.2s" }}></span>
                        </div>
                      </div>
                    </div>
                  )}
                  <div ref={messagesEndRef} />
                </div>

                {/* Input Area */}
                <div className="border-t border-border p-4">
                  <div className="flex gap-4">
                    <input
                      type="text"
                      value={input}
                      onChange={(e) => setInput(e.target.value)}
                      onKeyPress={(e) => e.key === "Enter" && sendMessage()}
                      placeholder="Ask Coach Alex anything..."
                      className="flex-1 bg-[#0B0F1A] border border-[#1E293B] rounded-lg px-4 py-3 text-white placeholder:text-gray-500 focus:border-primary outline-none"
                      data-testid="coach-input"
                    />
                    <button
                      onClick={sendMessage}
                      disabled={loading || !input.trim()}
                      className="bg-primary text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-600 transition-colors disabled:opacity-50"
                      data-testid="coach-send"
                    >
                      <Send className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              </div>
            )}
          </motion.div>
        </div>
      </div>
    </div>
  );
};

// ==================== FORM ANALYSIS ====================
const FormAnalysis = () => {
  const { user } = useAuth();
  const [mediaFile, setMediaFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [mediaType, setMediaType] = useState(null); // 'image' or 'video'
  const [exercise, setExercise] = useState("");
  const [concerns, setConcerns] = useState("");
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("upload"); // upload, history

  const canUseFormAnalysis = user?.subscription_tier === "elite" || user?.role === "admin";

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setMediaFile(file);
      const isVideo = file.type.startsWith('video/');
      setMediaType(isVideo ? 'video' : 'image');
      
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const analyzeForm = async () => {
    if (!exercise) {
      toast.error("Please specify the exercise name");
      return;
    }

    setLoading(true);
    try {
      let response;
      
      if (mediaFile && mediaType === 'image') {
        // For images, convert to base64 and use the analyze-form endpoint
        const reader = new FileReader();
        const base64Promise = new Promise((resolve) => {
          reader.onloadend = () => {
            const base64 = reader.result.split(",")[1];
            resolve(base64);
          };
          reader.readAsDataURL(mediaFile);
        });
        
        const base64Image = await base64Promise;
        
        response = await fetch(`${API}/ai/analyze-form`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({
            image: base64Image,
            exercise: exercise
          })
        });
      } else {
        // For text-only or video (send description to AI Coach)
        const analysisPrompt = `As an expert strength and conditioning coach, please analyze form for the following exercise:

Exercise: ${exercise}
${concerns ? `User's specific concerns: ${concerns}` : ''}
${mediaFile && mediaType === 'video' ? `Note: User has uploaded a video for reference but I'll provide general form guidance.` : ''}

Please provide:
1. Key form cues for this exercise (5-7 bullet points)
2. Common mistakes to avoid
3. Safety considerations
4. Progressive tips for improvement

Be specific and actionable.`;

        response = await fetch(`${API}/ai/coach-chat`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({
            message: analysisPrompt,
            session_id: `form-analysis-${user?.user_id || 'anon'}`
          })
        });
      }
      
      const data = await response.json();
      
      if (response.ok) {
        // Handle different response formats
        const analysisResult = data.analysis || data.response || data.message;
        if (analysisResult) {
          setAnalysis(analysisResult);
          toast.success("Form analyzed successfully!");
        } else {
          toast.error("No analysis received");
        }
      } else {
        toast.error(data.detail || "Analysis failed");
      }
    } catch (error) {
      console.error("Form analysis error:", error);
      toast.error("Failed to analyze form");
    } finally {
      setLoading(false);
    }
  };

  const clearAnalysis = () => {
    setMediaFile(null);
    setPreview(null);
    setMediaType(null);
    setExercise("");
    setConcerns("");
    setAnalysis(null);
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      
      <div className="pt-24 sm:pt-32 pb-24 sm:pb-12 px-4 sm:px-6">
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <h1 className="font-heading text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight mb-4">
              AI FORM <span className="gradient-text">ANALYSIS</span>
            </h1>
            <p className="text-text-secondary mb-8">
              Get AI-powered feedback on your exercise technique
            </p>

            {!canUseFormAnalysis ? (
              <div className="bg-surface border border-border rounded-xl p-8 text-center">
                <Lock className="w-12 h-12 text-text-muted mx-auto mb-4" />
                <h3 className="font-heading text-xl font-bold mb-2">Elite Feature</h3>
                <p className="text-text-secondary mb-4">
                  Form Analysis is exclusive to Elite Custom Coaching plan
                </p>
                <Link to="/pricing" className="text-primary hover:underline">
                  Upgrade to Elite
                </Link>
              </div>
            ) : (
              <div className="space-y-6">
                {/* Tabs */}
                <div className="flex gap-2 border-b border-border pb-4">
                  <button
                    onClick={() => setActiveTab("upload")}
                    className={`px-4 py-2 rounded-lg font-semibold transition-colors ${
                      activeTab === "upload" ? "bg-primary text-white" : "bg-[#1E293B] text-text-secondary hover:bg-[#2D3B4F]"
                    }`}
                  >
                    <Upload className="w-4 h-4 inline mr-2" />
                    New Analysis
                  </button>
                  <button
                    onClick={() => setActiveTab("tips")}
                    className={`px-4 py-2 rounded-lg font-semibold transition-colors ${
                      activeTab === "tips" ? "bg-primary text-white" : "bg-[#1E293B] text-text-secondary hover:bg-[#2D3B4F]"
                    }`}
                  >
                    <Lightbulb className="w-4 h-4 inline mr-2" />
                    Form Tips
                  </button>
                </div>

                {activeTab === "upload" ? (
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Upload Section */}
                    <div className="bg-surface border border-border rounded-xl p-6">
                      <h3 className="font-heading text-xl font-bold mb-6">UPLOAD MEDIA</h3>
                      
                      <div className="space-y-4">
                        <div
                          className="border-2 border-dashed border-[#1E293B] rounded-xl p-6 text-center cursor-pointer hover:border-primary/50 transition-colors"
                          onClick={() => document.getElementById("media-upload").click()}
                          data-testid="media-upload-zone"
                        >
                          {preview ? (
                            mediaType === 'video' ? (
                              <video src={preview} controls className="max-h-48 mx-auto rounded-lg" />
                            ) : (
                              <img src={preview} alt="Preview" className="max-h-48 mx-auto rounded-lg" />
                            )
                          ) : (
                            <>
                              <div className="flex justify-center gap-4 mb-4">
                                <Camera className="w-10 h-10 text-text-muted" />
                                <Video className="w-10 h-10 text-text-muted" />
                              </div>
                              <p className="text-text-secondary">Click to upload image or video</p>
                              <p className="text-text-muted text-sm mt-1">PNG, JPG, MP4, MOV up to 50MB</p>
                            </>
                          )}
                        </div>
                        <input
                          type="file"
                          id="media-upload"
                          accept="image/*,video/*"
                          onChange={handleFileChange}
                          className="hidden"
                          data-testid="form-media-upload"
                        />
                        
                        {preview && (
                          <button
                            onClick={() => { setMediaFile(null); setPreview(null); setMediaType(null); }}
                            className="text-sm text-destructive hover:underline"
                          >
                            Remove media
                          </button>
                        )}
                        
                        <div>
                          <label className="block text-sm text-text-secondary mb-2">Exercise Name *</label>
                          <input
                            type="text"
                            value={exercise}
                            onChange={(e) => setExercise(e.target.value)}
                            placeholder="e.g., Squat, Deadlift, Bench Press"
                            className="w-full bg-[#0B0F1A] border border-[#1E293B] rounded-lg px-4 py-3 text-white"
                            data-testid="form-exercise"
                          />
                        </div>

                        <div>
                          <label className="block text-sm text-text-secondary mb-2">Specific Concerns (optional)</label>
                          <textarea
                            value={concerns}
                            onChange={(e) => setConcerns(e.target.value)}
                            placeholder="e.g., I feel pain in my lower back during the lift, my knees cave inward..."
                            rows={3}
                            className="w-full bg-[#0B0F1A] border border-[#1E293B] rounded-lg px-4 py-3 text-white resize-none"
                            data-testid="form-concerns"
                          />
                        </div>
                        
                        <button
                          onClick={analyzeForm}
                          disabled={loading || !exercise}
                          className="w-full bg-primary text-white py-3 rounded-lg font-semibold hover:bg-blue-600 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                          data-testid="analyze-btn"
                        >
                          {loading ? (
                            <>
                              <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-white"></div>
                              Analyzing...
                            </>
                          ) : (
                            <>
                              <Sparkles className="w-5 h-5" />
                              Analyze Form
                            </>
                          )}
                        </button>
                      </div>
                    </div>

                    {/* Results Section */}
                    <div className="bg-surface border border-border rounded-xl p-6">
                      <div className="flex items-center justify-between mb-6">
                        <h3 className="font-heading text-xl font-bold">ANALYSIS RESULTS</h3>
                        {analysis && (
                          <button
                            onClick={clearAnalysis}
                            className="text-sm text-text-muted hover:text-white"
                          >
                            Clear
                          </button>
                        )}
                      </div>
                      
                      {analysis ? (
                        <div className="prose prose-invert max-w-none">
                          <div className="bg-[#0B0F1A] rounded-lg p-4 border border-[#1E293B]">
                            <p className="text-text-secondary whitespace-pre-wrap leading-relaxed">{analysis}</p>
                          </div>
                        </div>
                      ) : (
                        <div className="text-center py-12">
                          <Camera className="w-16 h-16 text-text-muted mx-auto mb-4" />
                          <p className="text-text-secondary">
                            Enter your exercise details to get AI-powered form feedback
                          </p>
                          <p className="text-text-muted text-sm mt-2">
                            You can optionally upload an image or video for more context
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  /* Form Tips Tab */
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {[
                      { exercise: "Squat", tips: ["Keep chest up", "Knees track over toes", "Hip crease below knee", "Weight on mid-foot"] },
                      { exercise: "Deadlift", tips: ["Neutral spine", "Bar over mid-foot", "Engage lats", "Push through floor"] },
                      { exercise: "Bench Press", tips: ["Retract shoulder blades", "Arch upper back", "Bar to lower chest", "Drive feet into floor"] },
                      { exercise: "Overhead Press", tips: ["Brace core tight", "Elbows slightly forward", "Bar path vertical", "Chin back at top"] },
                      { exercise: "Pull-up", tips: ["Full hang start", "Lead with chest", "Elbows to pockets", "Control the descent"] },
                      { exercise: "Romanian Deadlift", tips: ["Soft knee bend", "Push hips back", "Feel hamstring stretch", "Keep bar close"] }
                    ].map((item, i) => (
                      <div key={i} className="bg-surface border border-border rounded-xl p-4">
                        <h4 className="font-heading font-bold text-primary mb-3">{item.exercise}</h4>
                        <ul className="space-y-2">
                          {item.tips.map((tip, j) => (
                            <li key={j} className="flex items-start gap-2 text-sm text-text-secondary">
                              <CheckCircle className="w-4 h-4 text-success flex-shrink-0 mt-0.5" />
                              {tip}
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </motion.div>
        </div>
      </div>
    </div>
  );
};

// ==================== NUTRITION PLANS (Elite Feature) ====================
const NutritionPlans = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState("overview");
  const [goals, setGoals] = useState({
    target: "maintenance", // maintenance, fat_loss, muscle_gain
    calories: 2200,
    protein: 150,
    carbs: 250,
    fats: 70
  });
  const [generating, setGenerating] = useState(false);
  const [mealPlan, setMealPlan] = useState(null);

  const canUseNutrition = user?.subscription_tier === "elite" || user?.role === "admin";

  const generateMealPlan = async () => {
    setGenerating(true);
    try {
      const response = await fetch(`${API}/ai/coach-chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          message: `Generate a one-day meal plan for someone with the following goals:
            - Goal: ${goals.target === "fat_loss" ? "Fat Loss" : goals.target === "muscle_gain" ? "Muscle Gain" : "Maintenance"}
            - Target Calories: ${goals.calories}
            - Protein: ${goals.protein}g
            - Carbs: ${goals.carbs}g
            - Fats: ${goals.fats}g
            
            Provide 4-5 meals with specific foods, portions, and macros for each meal. Format as a structured plan.`,
          session_id: `nutrition-${user?.user_id || 'anon'}`
        })
      });
      
      const data = await response.json();
      if (data.response) {
        setMealPlan(data.response);
        toast.success("Meal plan generated!");
      }
    } catch (error) {
      toast.error("Failed to generate meal plan");
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      
      <div className="pt-24 sm:pt-32 pb-24 sm:pb-12 px-4 sm:px-6">
        <div className="max-w-5xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <h1 className="font-heading text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight mb-4">
              NUTRITION <span className="gradient-text">PLANS</span>
            </h1>
            <p className="text-text-secondary mb-8">
              Personalized nutrition guidance to fuel your performance
            </p>

            {!canUseNutrition ? (
              <div className="bg-surface border border-border rounded-xl p-8 text-center">
                <Lock className="w-12 h-12 text-text-muted mx-auto mb-4" />
                <h3 className="font-heading text-xl font-bold mb-2">Elite Feature</h3>
                <p className="text-text-secondary mb-4">
                  Personalized Nutrition Plans are exclusive to Elite Custom Coaching
                </p>
                <Link to="/pricing" className="text-primary hover:underline">Upgrade to Elite</Link>
              </div>
            ) : (
              <div className="space-y-6">
                {/* Tabs */}
                <div className="flex gap-2 border-b border-border pb-4 overflow-x-auto">
                  {[
                    { id: "overview", label: "Overview", icon: Target },
                    { id: "calculator", label: "Macro Calculator", icon: Calculator },
                    { id: "mealplan", label: "AI Meal Plan", icon: Sparkles }
                  ].map(tab => (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold whitespace-nowrap transition-colors ${
                        activeTab === tab.id ? "bg-primary text-white" : "bg-[#1E293B] text-text-secondary hover:bg-[#2D3B4F]"
                      }`}
                    >
                      <tab.icon className="w-4 h-4" />
                      {tab.label}
                    </button>
                  ))}
                </div>

                {activeTab === "overview" && (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {/* Current Macros Card */}
                    <div className="bg-surface border border-border rounded-xl p-6">
                      <h3 className="font-heading text-lg font-bold mb-4 flex items-center gap-2">
                        <Target className="w-5 h-5 text-primary" />
                        Daily Targets
                      </h3>
                      <div className="space-y-3">
                        <div className="flex justify-between items-center">
                          <span className="text-text-secondary">Calories</span>
                          <span className="font-bold text-white">{goals.calories} kcal</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-text-secondary">Protein</span>
                          <span className="font-bold text-success">{goals.protein}g</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-text-secondary">Carbs</span>
                          <span className="font-bold text-accent">{goals.carbs}g</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-text-secondary">Fats</span>
                          <span className="font-bold text-warning">{goals.fats}g</span>
                        </div>
                      </div>
                    </div>

                    {/* Quick Tips */}
                    <div className="bg-surface border border-border rounded-xl p-6">
                      <h3 className="font-heading text-lg font-bold mb-4 flex items-center gap-2">
                        <Lightbulb className="w-5 h-5 text-accent" />
                        Quick Tips
                      </h3>
                      <ul className="space-y-2 text-sm text-text-secondary">
                        <li className="flex items-start gap-2">
                          <CheckCircle className="w-4 h-4 text-success mt-0.5 flex-shrink-0" />
                          Eat protein with every meal (20-40g)
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle className="w-4 h-4 text-success mt-0.5 flex-shrink-0" />
                          Stay hydrated: 1oz per lb bodyweight
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle className="w-4 h-4 text-success mt-0.5 flex-shrink-0" />
                          Time carbs around workouts
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle className="w-4 h-4 text-success mt-0.5 flex-shrink-0" />
                          Don't fear fats - they support hormones
                        </li>
                      </ul>
                    </div>

                    {/* Get Started */}
                    <div className="bg-gradient-to-br from-primary/20 to-accent/20 border border-primary/30 rounded-xl p-6">
                      <h3 className="font-heading text-lg font-bold mb-4">Get Started</h3>
                      <p className="text-text-secondary text-sm mb-4">
                        Use the Macro Calculator to set your targets, then generate an AI meal plan tailored to your goals.
                      </p>
                      <button
                        onClick={() => setActiveTab("calculator")}
                        className="w-full bg-primary text-white py-2 rounded-lg font-semibold hover:bg-blue-600 transition-colors"
                      >
                        Calculate Macros
                      </button>
                    </div>
                  </div>
                )}

                {activeTab === "calculator" && (
                  <div className="bg-surface border border-border rounded-xl p-6 max-w-2xl">
                    <h3 className="font-heading text-xl font-bold mb-6">Set Your Targets</h3>
                    
                    <div className="space-y-6">
                      <div>
                        <label className="block text-sm text-text-secondary mb-2">Goal</label>
                        <div className="grid grid-cols-3 gap-2">
                          {[
                            { id: "fat_loss", label: "Fat Loss" },
                            { id: "maintenance", label: "Maintain" },
                            { id: "muscle_gain", label: "Build Muscle" }
                          ].map(g => (
                            <button
                              key={g.id}
                              onClick={() => setGoals({ ...goals, target: g.id })}
                              className={`py-2 px-4 rounded-lg font-semibold transition-colors ${
                                goals.target === g.id ? "bg-primary text-white" : "bg-[#1E293B] text-text-secondary hover:bg-[#2D3B4F]"
                              }`}
                            >
                              {g.label}
                            </button>
                          ))}
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm text-text-secondary mb-2">Daily Calories</label>
                          <input
                            type="number"
                            value={goals.calories}
                            onChange={(e) => setGoals({ ...goals, calories: parseInt(e.target.value) || 0 })}
                            className="w-full bg-[#0B0F1A] border border-[#1E293B] rounded-lg px-4 py-3 text-white"
                          />
                        </div>
                        <div>
                          <label className="block text-sm text-text-secondary mb-2">Protein (g)</label>
                          <input
                            type="number"
                            value={goals.protein}
                            onChange={(e) => setGoals({ ...goals, protein: parseInt(e.target.value) || 0 })}
                            className="w-full bg-[#0B0F1A] border border-[#1E293B] rounded-lg px-4 py-3 text-white"
                          />
                        </div>
                        <div>
                          <label className="block text-sm text-text-secondary mb-2">Carbs (g)</label>
                          <input
                            type="number"
                            value={goals.carbs}
                            onChange={(e) => setGoals({ ...goals, carbs: parseInt(e.target.value) || 0 })}
                            className="w-full bg-[#0B0F1A] border border-[#1E293B] rounded-lg px-4 py-3 text-white"
                          />
                        </div>
                        <div>
                          <label className="block text-sm text-text-secondary mb-2">Fats (g)</label>
                          <input
                            type="number"
                            value={goals.fats}
                            onChange={(e) => setGoals({ ...goals, fats: parseInt(e.target.value) || 0 })}
                            className="w-full bg-[#0B0F1A] border border-[#1E293B] rounded-lg px-4 py-3 text-white"
                          />
                        </div>
                      </div>

                      <div className="bg-[#0B0F1A] rounded-lg p-4 border border-[#1E293B]">
                        <p className="text-sm text-text-muted">
                          <strong>Tip:</strong> For most athletes, aim for 0.8-1g protein per lb bodyweight, 
                          0.3-0.4g fats per lb, and fill remaining calories with carbs.
                        </p>
                      </div>

                      <button
                        onClick={() => { toast.success("Targets saved!"); setActiveTab("mealplan"); }}
                        className="w-full bg-primary text-white py-3 rounded-lg font-semibold hover:bg-blue-600 transition-colors"
                      >
                        Save & Generate Meal Plan
                      </button>
                    </div>
                  </div>
                )}

                {activeTab === "mealplan" && (
                  <div className="space-y-6">
                    <div className="bg-surface border border-border rounded-xl p-6">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="font-heading text-xl font-bold">AI Meal Plan</h3>
                        <button
                          onClick={generateMealPlan}
                          disabled={generating}
                          className="flex items-center gap-2 bg-primary text-white px-4 py-2 rounded-lg font-semibold hover:bg-blue-600 transition-colors disabled:opacity-50"
                        >
                          {generating ? (
                            <>
                              <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white"></div>
                              Generating...
                            </>
                          ) : (
                            <>
                              <Sparkles className="w-4 h-4" />
                              Generate Plan
                            </>
                          )}
                        </button>
                      </div>
                      
                      <p className="text-sm text-text-muted mb-4">
                        Based on your targets: {goals.calories} kcal | {goals.protein}g protein | {goals.carbs}g carbs | {goals.fats}g fats
                      </p>

                      {mealPlan ? (
                        <div className="bg-[#0B0F1A] rounded-lg p-4 border border-[#1E293B]">
                          <p className="text-text-secondary whitespace-pre-wrap leading-relaxed">{mealPlan}</p>
                        </div>
                      ) : (
                        <div className="text-center py-12">
                          <Utensils className="w-16 h-16 text-text-muted mx-auto mb-4" />
                          <p className="text-text-secondary">Click "Generate Plan" to create a personalized meal plan</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
          </motion.div>
        </div>
      </div>
    </div>
  );
};

// ==================== 1-ON-1 COACHING (Elite Feature) ====================
const CoachingPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("book");
  const [sessionTypes, setSessionTypes] = useState([]);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [myRequests, setMyRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedType, setSelectedType] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [topic, setTopic] = useState("program_review");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const canUseCoaching = user?.subscription_tier === "elite" || user?.role === "admin";

  useEffect(() => {
    if (canUseCoaching) {
      fetchData();
    }
  }, [canUseCoaching]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [typesRes, slotsRes, requestsRes] = await Promise.all([
        fetch(`${API}/coaching/session-types`, { credentials: "include" }),
        fetch(`${API}/coaching/availability`, { credentials: "include" }),
        fetch(`${API}/coaching/my-requests`, { credentials: "include" })
      ]);
      
      if (typesRes.ok) {
        const data = await typesRes.json();
        setSessionTypes(data.session_types || []);
        if (data.session_types?.length > 0) {
          setSelectedType(data.session_types[0]);
        }
      }
      if (slotsRes.ok) {
        const data = await slotsRes.json();
        setAvailableSlots(data.slots || []);
      }
      if (requestsRes.ok) {
        const data = await requestsRes.json();
        setMyRequests(data.requests || []);
      }
    } catch (error) {
      console.error("Error fetching coaching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleBookSession = async () => {
    if (!selectedType || !selectedSlot) {
      toast.error("Please select a session type and time slot");
      return;
    }

    setSubmitting(true);
    try {
      const response = await fetch(`${API}/coaching/request`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          session_type_id: selectedType.session_type_id,
          slot_id: selectedSlot.slot_id,
          topic,
          message
        })
      });

      const data = await response.json();
      if (response.ok && data.checkout_url) {
        // Redirect to Stripe checkout
        window.location.href = data.checkout_url;
      } else {
        toast.error(data.detail || "Failed to create booking");
      }
    } catch (error) {
      toast.error("Failed to create booking");
    } finally {
      setSubmitting(false);
    }
  };

  const topics = [
    { id: "program_review", label: "Program Review", desc: "Get feedback on your training" },
    { id: "form_check", label: "Form Check", desc: "Analyze your technique" },
    { id: "nutrition", label: "Nutrition", desc: "Personalized nutrition guidance" },
    { id: "goal_setting", label: "Goal Setting", desc: "Plan your fitness goals" },
    { id: "injury_rehab", label: "Injury/Rehab", desc: "Return-to-sport guidance" },
    { id: "other", label: "Other", desc: "Custom topic" }
  ];

  const formatDate = (dateStr) => {
    const date = new Date(dateStr + "T00:00:00");
    return date.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: "bg-yellow-500/20 text-yellow-400",
      approved: "bg-green-500/20 text-green-400",
      declined: "bg-red-500/20 text-red-400",
      completed: "bg-blue-500/20 text-blue-400"
    };
    return <span className={`px-2 py-1 rounded text-xs font-medium ${styles[status] || styles.pending}`}>{status}</span>;
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      
      <div className="pt-24 sm:pt-32 pb-24 sm:pb-12 px-4 sm:px-6">
        <div className="max-w-5xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <h1 className="font-heading text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight mb-4">
              1-ON-1 <span className="gradient-text">COACHING</span>
            </h1>
            <p className="text-text-secondary mb-8">
              Book a personal coaching session via Zoom
            </p>

            {!canUseCoaching ? (
              <div className="bg-surface border border-border rounded-xl p-8 text-center">
                <Lock className="w-12 h-12 text-text-muted mx-auto mb-4" />
                <h3 className="font-heading text-xl font-bold mb-2">Elite Feature</h3>
                <p className="text-text-secondary mb-4">
                  1-on-1 Coaching is exclusive to Elite Custom Coaching plan
                </p>
                <Link to="/pricing" className="text-primary hover:underline">Upgrade to Elite</Link>
              </div>
            ) : loading ? (
              <div className="flex justify-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
              </div>
            ) : (
              <div className="space-y-6">
                {/* Tabs */}
                <div className="flex gap-2 border-b border-border pb-4">
                  <button
                    onClick={() => setActiveTab("book")}
                    className={`px-4 py-2 rounded-lg font-semibold transition-colors ${
                      activeTab === "book" ? "bg-primary text-white" : "bg-[#1E293B] text-text-secondary hover:bg-[#2D3B4F]"
                    }`}
                  >
                    Book Session
                  </button>
                  <button
                    onClick={() => setActiveTab("requests")}
                    className={`px-4 py-2 rounded-lg font-semibold transition-colors flex items-center gap-2 ${
                      activeTab === "requests" ? "bg-primary text-white" : "bg-[#1E293B] text-text-secondary hover:bg-[#2D3B4F]"
                    }`}
                  >
                    My Bookings
                    {myRequests.length > 0 && (
                      <span className="bg-accent text-white text-xs px-2 py-0.5 rounded-full">{myRequests.length}</span>
                    )}
                  </button>
                </div>

                {activeTab === "book" ? (
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Session Types */}
                    <div className="lg:col-span-1">
                      <div className="bg-surface border border-border rounded-xl p-6">
                        <h3 className="font-heading text-lg font-bold mb-4">Choose Session Type</h3>
                        <div className="space-y-3">
                          {sessionTypes.map(type => (
                            <button
                              key={type.session_type_id}
                              onClick={() => setSelectedType(type)}
                              className={`w-full text-left p-4 rounded-lg border transition-all ${
                                selectedType?.session_type_id === type.session_type_id
                                  ? "border-primary bg-primary/10"
                                  : "border-[#1E293B] bg-[#0B0F1A] hover:border-[#1E293B]"
                              }`}
                              data-testid={`session-type-${type.session_type_id}`}
                            >
                              <div className="flex justify-between items-start mb-2">
                                <span className="font-semibold text-white">{type.name}</span>
                                <span className="font-bold text-primary">${type.price_cents / 100}</span>
                              </div>
                              <p className="text-xs text-text-muted">{type.description}</p>
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>

                    {/* Booking Form */}
                    <div className="lg:col-span-2 space-y-4">
                      {/* Available Time Slots */}
                      <div className="bg-surface border border-border rounded-xl p-6">
                        <h3 className="font-heading text-lg font-bold mb-4">Select Time Slot</h3>
                        {availableSlots.length === 0 ? (
                          <div className="text-center py-8">
                            <Calendar className="w-12 h-12 text-text-muted mx-auto mb-4" />
                            <p className="text-text-secondary">No available slots at the moment</p>
                            <p className="text-text-muted text-sm">Check back soon for new availability</p>
                          </div>
                        ) : (
                          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                            {availableSlots.map(slot => (
                              <button
                                key={slot.slot_id}
                                onClick={() => setSelectedSlot(slot)}
                                className={`p-3 rounded-lg border text-center transition-all ${
                                  selectedSlot?.slot_id === slot.slot_id
                                    ? "border-primary bg-primary/10"
                                    : "border-[#1E293B] bg-[#0B0F1A] hover:border-[#1E293B]"
                                }`}
                                data-testid={`time-slot-${slot.slot_id}`}
                              >
                                <p className="text-sm font-semibold text-white">{formatDate(slot.date)}</p>
                                <p className="text-xs text-text-muted">{slot.start_time} - {slot.end_time}</p>
                              </button>
                            ))}
                          </div>
                        )}
                      </div>

                      {/* Topic Selection */}
                      <div className="bg-surface border border-border rounded-xl p-6">
                        <h3 className="font-heading text-lg font-bold mb-4">What do you want to discuss?</h3>
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                          {topics.map(t => (
                            <button
                              key={t.id}
                              onClick={() => setTopic(t.id)}
                              className={`p-3 rounded-lg border text-left transition-all ${
                                topic === t.id
                                  ? "border-primary bg-primary/10"
                                  : "border-[#1E293B] bg-[#0B0F1A] hover:border-[#1E293B]"
                              }`}
                            >
                              <p className="text-sm font-semibold text-white">{t.label}</p>
                              <p className="text-xs text-text-muted">{t.desc}</p>
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* Message */}
                      <div className="bg-surface border border-border rounded-xl p-6">
                        <h3 className="font-heading text-lg font-bold mb-4">Additional Details (Optional)</h3>
                        <textarea
                          value={message}
                          onChange={(e) => setMessage(e.target.value)}
                          placeholder="Anything specific you'd like to discuss or prepare for?"
                          rows={3}
                          className="w-full bg-[#0B0F1A] border border-[#1E293B] rounded-lg px-4 py-3 text-white resize-none"
                        />
                      </div>

                      {/* Book Button */}
                      <button
                        onClick={handleBookSession}
                        disabled={submitting || !selectedType || !selectedSlot}
                        className="w-full bg-primary text-white py-4 rounded-xl font-semibold hover:bg-blue-600 transition-colors disabled:opacity-50 flex items-center justify-center gap-2 text-lg"
                        data-testid="book-session-btn"
                      >
                        {submitting ? (
                          <>
                            <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-white"></div>
                            Processing...
                          </>
                        ) : (
                          <>
                            <DollarSign className="w-5 h-5" />
                            Pay ${selectedType?.price_cents ? selectedType.price_cents / 100 : 0} & Book Session
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                ) : (
                  /* My Requests Tab */
                  <div className="space-y-4">
                    {myRequests.length === 0 ? (
                      <div className="bg-surface border border-border rounded-xl p-8 text-center">
                        <Calendar className="w-16 h-16 text-text-muted mx-auto mb-4" />
                        <h3 className="font-heading text-xl font-bold mb-2">No Bookings Yet</h3>
                        <p className="text-text-secondary mb-4">Book your first coaching session to get started</p>
                        <button
                          onClick={() => setActiveTab("book")}
                          className="bg-primary text-white px-6 py-2 rounded-lg font-semibold hover:bg-blue-600 transition-colors"
                        >
                          Book a Session
                        </button>
                      </div>
                    ) : (
                      myRequests.map(req => (
                        <div key={req.request_id} className="bg-surface border border-border rounded-xl p-6">
                          <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
                            <div>
                              <h4 className="font-heading font-bold text-lg text-white">{req.session_type_name}</h4>
                              <p className="text-text-secondary text-sm">
                                {formatDate(req.slot_date)} at {req.slot_time}
                              </p>
                            </div>
                            <div className="flex items-center gap-2">
                              {getStatusBadge(req.status)}
                              {getStatusBadge(req.payment_status === "paid" ? "paid" : "unpaid")}
                            </div>
                          </div>
                          
                          {req.status === "approved" && req.zoom_link && (
                            <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4 mb-4">
                              <p className="text-sm text-green-400 font-semibold mb-2">Session Approved!</p>
                              <a
                                href={req.zoom_link}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-2 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors"
                              >
                                <Video className="w-4 h-4" />
                                Join Zoom Meeting
                              </a>
                              {req.coach_notes && (
                                <p className="text-sm text-text-secondary mt-2">
                                  <strong>Coach Notes:</strong> {req.coach_notes}
                                </p>
                              )}
                            </div>
                          )}

                          <div className="text-sm text-text-muted">
                            <p><strong>Topic:</strong> {topics.find(t => t.id === req.topic)?.label || req.topic}</p>
                            {req.message && <p className="mt-1"><strong>Your Message:</strong> {req.message}</p>}
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>
            )}
          </motion.div>
        </div>
      </div>
    </div>
  );
};

// ==================== COACHING SUCCESS PAGE ====================
const CoachingSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = searchParams.get("session_id");
  const [status, setStatus] = useState("loading");

  useEffect(() => {
    if (sessionId) {
      // The request status will be updated by webhook, just show success
      setStatus("success");
    }
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="pt-24 sm:pt-32 pb-24 sm:pb-12 px-4 sm:px-6">
        <div className="max-w-xl mx-auto text-center">
          <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}>
            <CheckCircle className="w-20 h-20 text-success mx-auto mb-6" />
            <h1 className="font-heading text-3xl font-bold mb-4">Booking Confirmed!</h1>
            <p className="text-text-secondary mb-6">
              Your coaching session has been booked successfully. You'll receive an email with your Zoom link once the coach confirms the session.
            </p>
            <div className="flex gap-4 justify-center">
              <Link
                to="/coaching"
                className="bg-primary text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-600 transition-colors"
              >
                View My Bookings
              </Link>
              <Link
                to="/dashboard"
                className="bg-[#1E293B] text-white px-6 py-3 rounded-lg font-semibold hover:bg-[#2D3B4F] transition-colors"
              >
                Back to Dashboard
              </Link>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};


export { AIGenerator, AICoach, FormAnalysis, NutritionPlans, CoachingPage, CoachingSuccessPage };
